import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FileRejection } from 'react-dropzone';
import { POI, PoiOption } from 'components/common/types/Planner.types';
import { addToastNotification } from 'store/notification/reducer';
import { CustomPoiFile } from '../PointsOfInterest.types';
import { CUSTOM_POI_DATA_PROVIDER, CUSTOM_POI_MAX_FILES } from '../consts';
import {
  parseFileRows,
  validateParsedRows,
  transformToPoiOptions,
  restoreCustomPoiState,
} from './customPOIUpload.utils';

interface UseCustomPOIUploadParams {
  onCategoryCreated?: (category: PoiOption) => void;
  onCategoryDeleted?: (category: PoiOption) => void;
  initialPois?: POI[];
  customPoiMaxRows?: number;
}

interface UseCustomPOIUpload {
  customPoiCategories: PoiOption[];
  customPoiFiles: CustomPoiFile[];
  onFileChange: (acceptedFiles: File[], rejectedFiles: FileRejection[]) => Promise<void>;
  onDeleteCustomCategory: (option: PoiOption) => void;
  hasReachedFileUploadLimit: boolean;
  isCustomCategory: (option: PoiOption) => boolean;
}

export const useCustomPOIUpload = ({
  onCategoryCreated,
  onCategoryDeleted,
  initialPois = [],
  customPoiMaxRows,
}: UseCustomPOIUploadParams = {}): UseCustomPOIUpload => {
  const dispatch = useDispatch();

  const [customPoiState, setCustomPoiState] = useState(() => restoreCustomPoiState(initialPois));
  const { files: customPoiFiles, categories: customPoiCategories } = customPoiState;

  const hasReachedFileUploadLimit = customPoiFiles.length >= CUSTOM_POI_MAX_FILES;

  const isCustomCategory = useCallback(
    (option: PoiOption): boolean => option.categoryPath?.startsWith(`${CUSTOM_POI_DATA_PROVIDER}/`) ?? false,
    [],
  );

  const processAcceptedFile = useCallback(
    async (file: File): Promise<void> => {
      if (customPoiFiles.some((existingFile) => existingFile.fileName === file.name)) {
        dispatch(addToastNotification({ type: 'Failure', description: 'Duplicate file.' }));
        return;
      }

      if (customPoiFiles.length >= CUSTOM_POI_MAX_FILES) return;

      try {
        const rows = await parseFileRows(file);
        const validation = validateParsedRows(rows, customPoiMaxRows);

        if (!validation.isValid) {
          dispatch(addToastNotification({ type: 'Failure', description: validation.error }));
          return;
        }

        const newFile: CustomPoiFile = { fileName: file.name, values: validation.values };
        const newCategory = transformToPoiOptions(file.name, validation.values);

        setCustomPoiState((prev) => ({
          files: [...prev.files, newFile],
          categories: [...prev.categories, newCategory],
        }));
        onCategoryCreated?.(newCategory);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Error processing file.';
        dispatch(addToastNotification({ type: 'Failure', description: message }));
      }
    },
    [dispatch, customPoiFiles, onCategoryCreated, customPoiMaxRows],
  );

  const onFileChange = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]): Promise<void> => {
      if (rejectedFiles.length > 0) {
        const errorCodes = new Set(rejectedFiles.flatMap((rejection) => rejection.errors.map((error) => error.code)));

        if (errorCodes.has('file-too-large')) {
          dispatch(addToastNotification({ type: 'Failure', description: 'File size cannot exceed 10MB.' }));
        }
        if (errorCodes.has('file-invalid-type')) {
          dispatch(addToastNotification({ type: 'Failure', description: 'File must be CSV, XLSX, or XLS.' }));
        }
        return;
      }

      await Promise.allSettled(acceptedFiles.map(processAcceptedFile));
    },
    [dispatch, processAcceptedFile],
  );

  const onDeleteCustomCategory = useCallback(
    (option: PoiOption): void => {
      setCustomPoiState((prev) => ({
        files: prev.files.filter((file) => file.fileName !== option.name),
        categories: prev.categories.filter((category) => category.name !== option.name),
      }));
      onCategoryDeleted?.(option);
    },
    [onCategoryDeleted],
  );

  return {
    customPoiCategories,
    customPoiFiles,
    onFileChange,
    onDeleteCustomCategory,
    hasReachedFileUploadLimit,
    isCustomCategory,
  };
};
